import React, { useState } from 'react'
import {
  Container,
  Breadcrumb,
  Row,
  Col,
  Table,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Nav,
} from 'react-bootstrap'
import { Link, useParams, useHistory } from 'react-router-dom'
import useTerminalDetail from 'hooks/use-terminal-detail'
import useAnsibleDetail from 'hooks/use-ansible-detail'
import useMacMinisByAnsible from 'hooks/use-mac-minis-by-ansible'
import usePhysicalPhonesByAnsible from 'hooks/use-physical-phones-by-ansible'
import { PnsMacMini, PnsMacMiniStatus } from 'interfaces/pns-mac-mini'
import { PhysicalPhone, PhoneStatus } from 'interfaces/physical-phone'
import useDebounce from 'hooks/use-debounce'
import AnsibleCapacityEditModal from 'components/ansible-capacity/ansible-capacity-edit-modal'

type DeviceType = 'mac-minis' | 'physical-phones'

const InventoryAnsibleDetailPage = () => {
  const { terminalId, ansibleId } = useParams<{
    terminalId: string
    ansibleId: string
  }>()
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [activeDeviceType, setActiveDeviceType] =
    useState<DeviceType>('mac-minis') // Default to Mac Minis
  const [showEditCapacityModal, setShowEditCapacityModal] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { data: terminal, isLoading: terminalLoading } =
    useTerminalDetail(terminalId)

  const { data: ansible, isLoading: ansibleLoading } =
    useAnsibleDetail(ansibleId)

  const {
    data: macMinisData,
    isLoading: macMinisLoading,
    error: macMinisError,
  } = useMacMinisByAnsible({
    ansibleId,
    page,
    limit: 20,
    search: activeDeviceType === 'mac-minis' ? debouncedSearchTerm : '',
    status: statusFilter,
    include: 'ansible',
  })

  const {
    data: phonesData,
    isLoading: phonesLoading,
    error: phonesError,
  } = usePhysicalPhonesByAnsible({
    ansibleId,
    page,
    limit: 20,
    search: activeDeviceType === 'physical-phones' ? debouncedSearchTerm : '',
    status: statusFilter,
    include: 'ansible,macMiniUser',
  })

  const handleMacMiniClick = (macMini: PnsMacMini) => {
    history.push(
      `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${macMini.id}`
    )
  }

  const handlePhoneClick = (phone: PhysicalPhone) => {
    history.push(
      `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/phone/${phone.id}`
    )
  }

  const getMacMiniStatusVariant = (status: PnsMacMiniStatus) => {
    switch (status) {
      case PnsMacMiniStatus.ACTIVE:
        return 'success'
      case PnsMacMiniStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniStatus.PROVISIONING:
        return 'info'
      case PnsMacMiniStatus.MAINTENANCE:
        return 'warning'
      case PnsMacMiniStatus.RELOCATING:
        return 'warning'
      case PnsMacMiniStatus.OFFLINE:
        return 'secondary'
      case PnsMacMiniStatus.DECOMMISSIONED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getPhoneStatusVariant = (status: PhoneStatus) => {
    switch (status) {
      case PhoneStatus.ACTIVE:
        return 'success'
      case PhoneStatus.AVAILABLE:
        return 'info'
      case PhoneStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PhoneStatus.WARMING:
        return 'info'
      case PhoneStatus.MAINTENANCE:
        return 'warning'
      case PhoneStatus.OFFLINE:
        return 'secondary'
      case PhoneStatus.DECOMMISSIONED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const handleDeviceTypeChange = (eventKey: string | null) => {
    if (eventKey) {
      setActiveDeviceType(eventKey as DeviceType)
      setPage(1) // Reset page when switching
      setSearchTerm('') // Reset search
      setStatusFilter('') // Reset status filter
    }
  }

  const currentData =
    activeDeviceType === 'mac-minis' ? macMinisData : phonesData
  const currentLoading =
    activeDeviceType === 'mac-minis' ? macMinisLoading : phonesLoading
  const currentError =
    activeDeviceType === 'mac-minis' ? macMinisError : phonesError

  if (currentError) {
    return (
      <Container>
        <Alert variant="danger">
          Error loading {activeDeviceType.replace('-', ' ')}:{' '}
          {currentError instanceof Error
            ? currentError.message
            : 'Unknown error'}
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item href={`/inventory/terminals/${terminalId}`}>
          {terminal?.name || 'Terminal'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{ansible?.name || 'Ansible'}</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{ansible?.name || 'Ansible'} Details</h1>
      </div>

      {/* Ansible Capacity Overview */}
      {ansible && (
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Capacity Overview</h4>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowEditCapacityModal(true)}
            >
              Edit Capacity
            </Button>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Mac Minis:</strong> {ansible.currentMacMinis} /{' '}
                  {ansible.maxMacMinis}
                  <div className="progress mt-2">
                    <div
                      className="progress-bar bg-info"
                      style={{
                        width: `${
                          (ansible.currentMacMinis / ansible.maxMacMinis) * 100
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Physical Phones:</strong>{' '}
                  {ansible.currentPhysicalPhones} / {ansible.maxPhysicalPhones}
                  <div className="progress mt-2">
                    <div
                      className="progress-bar bg-success"
                      style={{
                        width: `${
                          (ansible.currentPhysicalPhones /
                            ansible.maxPhysicalPhones) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Device Type Toggle */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Nav
            variant="tabs"
            activeKey={activeDeviceType}
            onSelect={handleDeviceTypeChange}
          >
            <Nav.Item>
              <Nav.Link eventKey="mac-minis">
                Mac Minis {ansible && `(${ansible.currentMacMinis})`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="physical-phones">
                Physical Phones{' '}
                {ansible && `(${ansible.currentPhysicalPhones})`}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={`Search ${activeDeviceType.replace(
                    '-',
                    ' '
                  )}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Control
                as="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                {activeDeviceType === 'mac-minis' ? (
                  <>
                    <option value="ACTIVE">Active</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="PROVISIONING">Provisioning</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="RELOCATING">Relocating</option>
                    <option value="OFFLINE">Offline</option>
                    <option value="DECOMMISSIONED">Decommissioned</option>
                  </>
                ) : (
                  <>
                    <option value="ACTIVE">Active</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="ICLOUD_SETUP_IN_PROGRESS">
                      iCloud Setup
                    </option>
                    <option value="WARMING">Warming</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="OFFLINE">Offline</option>
                    <option value="DECOMMISSIONED">Decommissioned</option>
                  </>
                )}
              </Form.Control>
            </Col>
          </Row>

          {/* Loading State */}
          {currentLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
              <div className="mt-2">
                Loading {activeDeviceType.replace('-', ' ')}...
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive variant="dark">
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#343a40',
                  }}
                >
                  <tr>
                    <th style={{ borderTop: 'none', width: '80px' }}>#</th>
                    <th style={{ borderTop: 'none', width: '150px' }}>Name</th>
                    <th style={{ borderTop: 'none', width: '120px' }}>
                      Status
                    </th>
                    {activeDeviceType === 'mac-minis' ? (
                      <>
                        <th style={{ borderTop: 'none', width: '120px' }}>
                          Model
                        </th>
                        <th style={{ borderTop: 'none', width: '150px' }}>
                          Users
                        </th>
                        <th style={{ borderTop: 'none' }}>IP Address</th>
                      </>
                    ) : (
                      <>
                        <th style={{ borderTop: 'none', width: '120px' }}>
                          Device Model
                        </th>
                        <th style={{ borderTop: 'none', width: '150px' }}>
                          Mac Mini User
                        </th>
                        <th style={{ borderTop: 'none' }}>IMEI</th>
                      </>
                    )}
                    <th style={{ borderTop: 'none', width: '130px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData?.data?.map((device, index) => (
                    <tr
                      key={device.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        activeDeviceType === 'mac-minis'
                          ? handleMacMiniClick(device as PnsMacMini)
                          : handlePhoneClick(device as PhysicalPhone)
                      }
                    >
                      <td>{(page - 1) * 20 + index + 1}</td>
                      <td>
                        <Link
                          to={
                            activeDeviceType === 'mac-minis'
                              ? `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${device.id}`
                              : `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/phone/${device.id}`
                          }
                          style={{
                            color: '#4da6ff',
                            textDecoration: 'underline',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {device.name}
                        </Link>
                      </td>
                      <td>
                        <Badge
                          variant={
                            activeDeviceType === 'mac-minis'
                              ? getMacMiniStatusVariant(
                                  (device as PnsMacMini).status
                                )
                              : getPhoneStatusVariant(
                                  (device as PhysicalPhone).status
                                )
                          }
                        >
                          {device.status}
                        </Badge>
                      </td>
                      {activeDeviceType === 'mac-minis' ? (
                        <>
                          <td>{(device as PnsMacMini).model}</td>
                          <td>
                            {(device as PnsMacMini).currentUsers} /{' '}
                            {(device as PnsMacMini).maxUsers}
                          </td>
                          <td>{(device as PnsMacMini).primaryIp || '-'}</td>
                        </>
                      ) : (
                        <>
                          <td>{(device as PhysicalPhone).deviceModel}</td>
                          <td>
                            {(device as PhysicalPhone).macMiniUser?.username ||
                              'Unassigned'}
                          </td>
                          <td>{(device as PhysicalPhone).imei || '-'}</td>
                        </>
                      )}
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            activeDeviceType === 'mac-minis'
                              ? handleMacMiniClick(device as PnsMacMini)
                              : handlePhoneClick(device as PhysicalPhone)
                          }
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {currentData?.data?.length === 0 && (
            <div className="text-center text-muted py-4">
              No {activeDeviceType.replace('-', ' ')} found matching your
              criteria
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Ansible Capacity Edit Modal */}
      {ansible && (
        <AnsibleCapacityEditModal
          show={showEditCapacityModal}
          onHide={() => setShowEditCapacityModal(false)}
          ansible={ansible}
          onSuccess={() => {
            // Refresh ansible data after successful update
            window.location.reload()
          }}
        />
      )}
    </Container>
  )
}

export default InventoryAnsibleDetailPage
